import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('Medication');
export interface Medication {
  id: string;
  userId: string;
  taskId: number;
  visibilityState: 'LOCKED' | 'UNLOCKED';
  medicationName: string;
  dosage: string;
  frequency: string;
  prescribingDoctor: string;
  pharmacy: string;
  conditionTreated: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMedicationData {
  taskId: number;
  medicationName: string;
  dosage: string;
  frequency: string;
  prescribingDoctor: string;
  pharmacy: string;
  conditionTreated: string;
  notes: string;
}

export interface UpdateMedicationData extends Partial<CreateMedicationData> {}

class MedicationService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  async getMedications(taskId: number, targetUserId?: string): Promise<Medication[]> {
    logger.debug(`=== MEDICATION SERVICE: Getting medications for task ${taskId} ===`);
    const params = targetUserId ? `?taskId=${taskId}&targetUserId=${targetUserId}` : `?taskId=${taskId}`;
    const response = await this.makeRequest<Medication[]>(`/api/medication${params}`);
    logger.debug(`=== MEDICATION SERVICE: Retrieved ${response?.length || 0} medications ===`);
    return response || [];
  }

  async createMedication(data: CreateMedicationData): Promise<Medication> {
    logger.debug(`=== MEDICATION SERVICE: Creating medication ===`, data);
    const response = await this.makeRequest<Medication>('/api/medication', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    logger.debug(`=== MEDICATION SERVICE: Created medication with ID ${response.id} ===`);
    return response;
  }

  async updateMedication(id: string, data: UpdateMedicationData): Promise<Medication> {
    logger.debug(`=== MEDICATION SERVICE: Updating medication ${id} ===`, data);
    const response = await this.makeRequest<Medication>(`/api/medication/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    logger.debug(`=== MEDICATION SERVICE: Updated medication ${id} ===`);
    return response;
  }

  async deleteMedication(id: string): Promise<void> {
    logger.debug(`=== MEDICATION SERVICE: Deleting medication ${id} ===`);
    await this.makeRequest<void>(`/api/medication/${id}`, {
      method: 'DELETE',
    });
    logger.debug(`=== MEDICATION SERVICE: Deleted medication ${id} ===`);
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest<{ taskId: number; completed: boolean }>(`/api/medication/complete/${taskId}`, {
      method: 'POST',
    });
    
    return response.completed;
  }

  async setVisibilityState(id: string, visibilityState: 'LOCKED' | 'UNLOCKED'): Promise<Medication> {
    logger.debug(`=== MEDICATION SERVICE: Setting visibility ${visibilityState} for ${id} ===`);
    return await this.makeRequest<Medication>(`/api/medication/${id}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ visibilityState })
    });
  }

  async getMedicationAccessGrants(id: string): Promise<any[]> {
    logger.debug(`=== MEDICATION SERVICE: Getting access grants for ${id} ===`);
    const response = await this.makeRequest<any>(`/api/medication/${id}/access-grants`);
    return response?.data || response || [];
  }

  async lockMedication(id: string): Promise<void> {
    logger.debug(`=== MEDICATION SERVICE: Locking medication ${id} ===`);
    await this.makeRequest<void>(`/api/medication/${id}/lock`, {
      method: 'POST',
    });
  }

  async unlockMedication(id: string, selectedEmails: string[]): Promise<Medication> {
    logger.debug(`=== MEDICATION SERVICE: Unlocking medication ${id} for emails:`, selectedEmails);
    return await this.makeRequest<Medication>(`/api/medication/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ selectedEmails })
    });
  }
}

export const medicationService = new MedicationService();